<script setup lang="ts">
import { computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { MediaVersion, VirusScanStatus } from '@roadfootage/api-client'
import { useMediaDownloadStore } from '@/stores/media-download'
import { formatFileSize } from '@/utils/format'

const props = defineProps<{
  modelValue: boolean
  userFileId: string | null
  fileName: string
}>()

const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>()

const store = useMediaDownloadStore()
const { versions, selected, virusScanStatus, needsConfirm, link, loading } = storeToRefs(store)

watch(
  () => [props.modelValue, props.userFileId] as const,
  ([open, id]) => {
    if (open && id) void store.open(id, props.fileName)
    if (!open) store.reset()
  },
  { immediate: true }
)

const close = () => emit('update:modelValue', false)

const scanColor = computed(() => {
  if (virusScanStatus.value === VirusScanStatus.Clean) return 'positive'
  if (virusScanStatus.value === VirusScanStatus.Threat) return 'negative'
  return 'warning'
})

// Only the original can carry an unscanned file; the chip names the scan state on that row.
const originalFlag = computed(() => {
  if (virusScanStatus.value === VirusScanStatus.Clean) return null
  return virusScanStatus.value === VirusScanStatus.Threat ? 'Threat' : 'Unscanned'
})

const selectedLabel = computed(() => selected.value ?? '')
const sizeLabel = (bytes: number | null | undefined) =>
  bytes == null ? 'size unknown' : formatFileSize(bytes)
</script>

<template>
  <q-dialog :model-value="modelValue" @update:model-value="emit('update:modelValue', $event)">
    <q-card style="min-width: 320px; max-width: 480px; width: 100%">
      <q-card-section class="row items-center no-wrap q-pb-none">
        <div class="text-h6 ellipsis col" :title="fileName">Download {{ fileName }}</div>
        <q-btn flat round dense icon="mdi-close" aria-label="Close" @click="close" />
      </q-card-section>

      <q-card-section v-if="virusScanStatus" class="q-py-xs text-caption">
        Virus scan:
        <q-badge :color="scanColor" :label="virusScanStatus" />
      </q-card-section>

      <q-separator class="q-mt-sm" />

      <q-card-section class="q-py-sm">
        <div v-if="loading && !versions.length" class="flex flex-center q-pa-md">
          <q-spinner />
        </div>
        <div v-else-if="!versions.length" class="text-grey-6">No versions available.</div>
        <q-list v-else>
          <q-item v-for="v in versions" :key="v.version" dense class="q-px-none">
            <q-item-section>
              <q-radio
                :model-value="selected"
                :val="v.version as MediaVersion"
                :label="v.version"
                @update:model-value="store.select($event as MediaVersion)"
              />
            </q-item-section>
            <q-item-section side class="row items-center no-wrap text-caption">
              <span>
                {{ sizeLabel(v.sizeBytes) }}
                <q-badge
                  v-if="v.requiresAcknowledgement && originalFlag"
                  :color="scanColor"
                  :label="originalFlag"
                  class="q-ml-sm"
                />
              </span>
            </q-item-section>
          </q-item>
        </q-list>

        <q-banner v-if="needsConfirm" rounded class="bg-warning text-black q-mt-sm">
          <template #avatar><q-icon name="mdi-alert" /></template>
          This original has not been scanned clean ({{ virusScanStatus }}). It may contain malware.
          Open it only in a safe environment.
        </q-banner>

        <div v-if="link" class="q-mt-md">
          <div class="text-caption q-mb-xs">Link ready. It expires in a few mins.</div>
          <q-btn
            color="primary"
            icon="mdi-download"
            :label="`Download ${selectedLabel}`"
            :href="link"
            target="_blank"
            rel="noopener"
          />
        </div>
      </q-card-section>

      <q-separator />

      <q-card-actions align="right">
        <q-btn v-if="link" flat label="Close" @click="close" />
        <template v-else>
          <q-btn flat label="Cancel" @click="close" />
          <q-btn
            v-if="needsConfirm"
            color="warning"
            text-color="black"
            label="I understand, get link"
            :loading="loading"
            @click="store.confirmRisk()"
          />
          <q-btn
            v-else
            color="primary"
            label="Get link"
            :disable="!selected"
            :loading="loading && !!selected"
            @click="store.requestLink()"
          />
        </template>
      </q-card-actions>
    </q-card>
  </q-dialog>
</template>
